import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Text,
  SimpleGrid,
  Alert,
  AlertIcon,
  Button,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Flex,
  Checkbox,
  Link as ChakraLink,
} from "@chakra-ui/react";

import theme from "../../theme"; // Adjust the import path if necessary
import "./Dashboard.css"; // Import the CSS file for hover effects and styling

import Chart from "../ChartComponents/Chart";
import BarChart from "../ChartComponents/BarChart";
import RadarChart from "../ChartComponents/RadarChart";
import LineChart from "../ChartComponents/LineChart";
import ProgressBar from "../ChartComponents/ProgressBar";

import { cqAPI, Auth, getIfGroupedByReplyStatus } from "../../lib/Auth";
import { useAppContext } from "../../AppContext";
import moment from "moment";

// Map conversation topics to 1-word categories
const topicCategoryMapping = {
  "Appointment Management": "Appointments",
  "Appointments and Visits": "Appointments",
  "Treatment and Services": "Treatment",
  "Personal Data Handling": "Data",
  "Medical Records": "Data",
  "Billing and Payments": "Billing",
  "Insurance Handling": "Insurance",
  "Promotions and Offers": "Promotions",
  "Customer Service": "Service",
  "Communication Preferences": "Service",
  "Health and Safety": "Health",
  "Dental Health": "Health",
  "COVID-19 Specific": "COVID",
  "Legal and Compliance": "Compliance",
  "Technology and Equipment": "Technology",
  "Product Information": "Product",
  Prescriptions: "Prescriptions",
};

const lines = [
  { name: "CurrentYear", dataKey: "CurrentYear", stroke: theme.colors.brand[30] },
  { name: "PreviousYear", dataKey: "PreviousYear", stroke: theme.colors.brand[20] },
];

// Color mapping for severity bubbles (we'll multiply severity by 20 to get 0–100)
const severityColors = [
  { min: 0, max: 30, color: "#C44C3B", label: "Critical" },
  { min: 31, max: 40, color: "#ED9230", label: "Severe" },
  { min: 41, max: 50, color: "#EBC030", label: "At Risk" },
  { min: 51, max: 60, color: "#F1CD3B", label: "Needs Improvement" },
  { min: 61, max: 70, color: "#55B83A", label: "Fair" },
  { min: 71, max: 80, color: "#3C9420", label: "Good" },
  { min: 81, max: 90, color: "#2B45C0", label: "Very Good" },
  { min: 91, max: 100, color: "#9E30F6", label: "Excellent" },
];

export default function Dashboard() {
  const { isPatient, menuLoaded, selectedBusiness } = useAppContext();
  const toast = useToast();
  const navigate = useNavigate();

  // Dashboard data and alerts
  const [alertData, setAlertData] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardAlerts, setDashboardAlerts] = useState([]);

  // States to handle incomplete treatments + score simulation
  const [incompleteTreatments, setIncompleteTreatments] = useState([]);
  const [selectedTreatments, setSelectedTreatments] = useState([]);
  const [simulatedScore, setSimulatedScore] = useState({});
  const [simulatedColor, setSimulatedColor] = useState({});

  // Modal open/close for Health Score details
  const [isHealthScoreModalOpen, setIsHealthScoreModalOpen] = useState(false);

  // Spinner/Disabled state for "See Details" button
  const [isHealthScoreModalLoading, setIsHealthScoreModalLoading] = useState(false);
  
  const updateDashboardAlerts = (alerts) => {
    const sortedAlerts = alerts.toSorted((a, b) => {
      if (a.priority === 'high' && b.priority === 'medium') {
        return -1;
      } else if (a.priority === 'medium' && b.priority === 'high') {
        return 1;
      }
      return 0;
    });
    const groupedAlerts = (sortedAlerts || []).reduce(
      (known, value) => {
        if (!value.msg) {
          return known;
        }
        const highAlerts = Object.values(known).filter(a => a.priority === 'high');
        if((!highAlerts.length) || (highAlerts.length && value.priority === 'high')) {
          known[value.msg] = value;
        }
        
        return known;
      },
      {},
    );
    setDashboardAlerts(Object.values(groupedAlerts));
  };

  // ------------------ Fetching Data ------------------
  // Fetch main dashboard data
  const fetchDashboardData = async () => {
    try {
      if (!isPatient && !selectedBusiness?.bid){return}
      const response = await cqAPI.get(`/dashboard`,{},0);
      if (response.type === "ok") {
        setDashboardData(response.data);
        updateDashboardAlerts(response?.data?.alerts);
      } else {
        setDashboardData({});
        toast({
          title: "Success",
          description: response.msg,
          status: "success",
          duration: 9000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    }
  };

  useEffect(() => {

    if (selectedBusiness?.deleted) {
      const humanReadableDate = moment(selectedBusiness.deleted).format('MMMM Do, YYYY');
      const deletionAlert = {
        priority: 'high',
        msg: `The data for this business was deleted on ${humanReadableDate}.`,
      }
      if (!dashboardAlerts.find(a=>a.msg === deletionAlert.msg)){
        setDashboardAlerts([deletionAlert, ...dashboardAlerts]);
      }
    } else if (selectedBusiness?.scheduled_for_deletion) {
      const humanReadableDate = moment(selectedBusiness.scheduled_for_deletion).format('MMMM Do, YYYY');
      const deletionAlert = {
        priority: 'high',
        msg: `This business is scheduled for deletion on ${humanReadableDate}. Please contact support if this is a mistake.`,
      }
      if (!dashboardAlerts.find(a=>a.msg === deletionAlert.msg)){
        setDashboardAlerts([deletionAlert, ...dashboardAlerts]);
      }
    }
  })

  // Fetch data for the open conversation alert (staff only)
  useEffect(() => {
    if (!menuLoaded){return}
    const fetchAlertData = async () => {
      try {
        if (isPatient || !selectedBusiness?.bid) return;

        const filter = getIfGroupedByReplyStatus() && `&filter=unanswered` || "";
        const response = await cqAPI.get(`/threads?openonly=1&${filter}type=email,sms,emailsms,voicemail,api&bids=${selectedBusiness.bid}`);
        if (response.type !== "ok") {
          throw new Error("Invalid response for dashboard alert");
        }

        const conversationDates = response.data.map((item) => moment(item.updated));
        const oldestConversation = conversationDates.reduce(
          (min, current) => (current < min ? current : min),
          conversationDates[0]
        );

        const now = moment();
        const daysSinceOldestConv = now.diff(oldestConversation, "days");

        setAlertData({
          numOpenConversations: conversationDates.length,
          daysSinceOldestConversation: daysSinceOldestConv,
        });
      } catch (error) {
        console.error("Failed to fetch alert data:", error);
      }
    };

    fetchDashboardData();
    fetchAlertData();
  }, [isPatient, menuLoaded]);

  // ------------------ Score Simulation ------------------
  const simulateScore = async (completed = selectedTreatments) => {
    setIsHealthScoreModalLoading(true);
    try {
      const completedWithMultiple = completed.map(id=>incompleteTreatments.find(t=>t.id === id)).reduce((acc, cur)=>{
        incompleteTreatments.filter(a=>a.name === cur.name && a.added_date === cur.added_date && a.proc_code === cur.proc_code).forEach(t=>acc.push(t.id))
        return acc
      },[]);
      
      const response = await cqAPI.post("/simulate-score", {
        completed : completedWithMultiple,
      });
      
      setIsHealthScoreModalLoading(false);

      if (response.type === "error") {
        toast({
          description: response.msg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return false;
      }

      setSimulatedScore(response.data);
      const selectedColor = severityColors.find(
        (v) => response.data.score >= v.min && response.data.score <= v.max
      );
      setSimulatedColor(selectedColor);
      return true;
    } catch (err) {
      toast({
        description: "Error simulating score",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
  };

  // ------------------ Modal Handlers ------------------
  const openHealthScoreModal = async () => {

    const healthScoreData = dashboardData?.healthScoreData?.[0];
    if (healthScoreData?.treatments?.length) {
      const needsCleaning = dashboardData.healthScoreData && dashboardData.healthScoreData[0]?.caption && dashboardData.healthScoreData[0].caption.includes('cleaning')? [{
        proc_code : 'Cleaning',
        name : 'Cleaning',
        id : 'Cleaning'
      }] : []
      
      setIncompleteTreatments(healthScoreData.treatments.filter((t) => t.name !== "Unknown").concat(needsCleaning));
    }

    // Simulate the score first
    const success = await simulateScore(selectedTreatments);
    if (success) {
      setIsHealthScoreModalOpen(true);
    }
  };

  const closeHealthScoreModal = () => {
    setIsHealthScoreModalOpen(false);
  };

  // When toggling a treatment's checkbox, immediately call simulateScore
  // with the *new* array of selected treatments
  const handleTreatmentToggle = (id) => {
    setSelectedTreatments((prevSelected) => {
      let updatedSelected;
      if (prevSelected.includes(id)) {
        updatedSelected = prevSelected.filter((code) => code !== id);
      } else {
        updatedSelected = [...prevSelected, id];
      }
      // Call simulateScore with the updated array
      simulateScore(updatedSelected);
      return updatedSelected;
    });
  };

  const handleMakeAppointment = () => {
    const newSearchParams = new URLSearchParams(window.location.search);
    navigate(`/appointments/new?${newSearchParams.toString()}`);
    closeHealthScoreModal();
  };

  const handleAskForMoreInfo = () => {
    if (!selectedTreatments.length) {
      toast({
        description: "Please select the treatments that you want to ask about",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Build the message template
    let message =
      "Will you please provide more information for the following unscheduled treatments on my file and how they impact my dental score:\n\n";
    // For each selected ID, find the corresponding treatment in `incompleteTreatments`
    selectedTreatments.forEach((treatmentId, index) => {
      const t = incompleteTreatments.find((tmt) => tmt.id === treatmentId);
      if (t) {
        const multiple = incompleteTreatments.filter(
          (a) => a.name === t.name && a.added_date === t.added_date && a.proc_code === t.proc_code
        ).length > 1;
        message += `${index + 1}- ${multiple ? "Multiple " : ""}${
          t.proc_code
        }${t.added_date? (' added on ' + t.added_date) : ''}\n`;
      }
    });

    const newSearchParams = new URLSearchParams(window.location.search);
    newSearchParams.set("message", encodeURIComponent(message));
    closeHealthScoreModal();
    navigate(`/inbox/${Auth.user.id}?${newSearchParams.toString()}`);
  };

  // ------------------ Helper / Data Formatting Functions ------------------
  const mapTopicsToCategories = (histogram, topN = 4) => {
    const categoryCounts = Object.entries(histogram || {}).reduce((acc, [topic, value]) => {
      const category = topicCategoryMapping[topic] || topic;
      acc[category] = (acc[category] || 0) + value;
      return acc;
    }, {});

    const categoryArray = Object.entries(categoryCounts).map(([name, value]) => ({
      name,
      value,
    }));
    categoryArray.sort((a, b) => b.value - a.value);

    const topCategories = categoryArray.slice(0, topN);
    const otherCategories = categoryArray.slice(topN);
    const othersValue = otherCategories.reduce((acc, curr) => acc + curr.value, 0);

    if (othersValue > 0) {
      topCategories.push({ name: "Others", value: othersValue });
    }

    return topCategories;
  };

  const transformSatisfactionData = (data) => {
    const fullMark = 100;
    return Object.entries(data || {}).map(([subject, value]) => ({
      subject,
      Satisfaction: (value || 0).toFixed(2),
      fullMark,
    }));
  };

  // helper to pick the correct color for severity bubble
  const getSeverityColor = (severity) => {
    // multiply severity (1–5) by 20 → 20–100
    const severityValue = 100 - severity * 20;
    const colorObj = severityColors.find((c) => severityValue >= c.min && severityValue <= c.max);
    return colorObj ? colorObj.color : "#777";
  };

  // Main function to adapt data for charts
  const formatDashboardData = (data) => {
    const formattedData = {};

    // 1) Communication Data
    if (data.communicationData) {
      formattedData.communicationData = {
        data: Object.entries(data.communicationData || {}).map(([name, value]) => ({
          name: name.charAt(0).toUpperCase() + name.slice(1),
          value,
        })),
      };
    }

    // 2) Appointment Data
    if (data.appointmentData) {
      formattedData.appointmentData = {
        data: data.appointmentData,
        caption:
          "This chart shows the status of your appointments. The sections represent upcoming, completed, and missed appointments.",
      };
    }

    // 3) Common Topics
    if (data.commonTopics) {
      formattedData.commonTopics = {
        data: mapTopicsToCategories(data.commonTopics, 3),
      };
    }

    // 4) Response Times
    if (data.responseTimes) {
      formattedData.responseTimes = {
        data: Object.entries(data.responseTimes || {}).map(([name, value]) => ({
          name,
          // convert seconds to hours, rounding to 2 decimal places
          ResponseTime: Math.round(((value || 0) / 3600) * 100) / 100,
        })),
      };
    }

    // 5) Extension Calls
    if (data.extCalls) {
      formattedData.extCalls = {
        data: (data.extCalls || []).map(({ numericValue, totalCalls }) => ({
          name: String(numericValue),
          value: totalCalls,
        })),
      };
    }

    // 6) Staff Performance
    if (data.staffPerformance) {
      formattedData.staffPerformance = {
        data: (data.staffPerformance || []).map(({ name, sms, email }) => {
          const initials = name
            ? name
                .split(" ")
                .map((word) => word[0].toUpperCase())
                .join("")
            : "";
          const initialShort =
            initials.length <= 2 ? initials : initials[0] + initials[initials.length - 1];
          return { fullname: name, name: initialShort, SMS: sms, Emails: email };
        }),
      };
    }

    // 7) Patient Satisfaction
    if (data.patientSatisfaction) {
      formattedData.patientSatisfaction = {
        data: transformSatisfactionData(data.patientSatisfaction),
      };
    }

    // 8) After-Hour Metrics
    if (Object.keys(data.afterHourMetrics || {}).length) {
      formattedData.afterHourMetrics = {
        data: Object.entries(data.afterHourMetrics).map(([subject, values]) => ({
          subject,
          Automated: values.Automated ? +values.Automated.toFixed(2) : 0,
          Manual: values.Manual ? +values.Manual.toFixed(2) : 0,
          fullMark: 100,
        })),
      };
    }

    // 9) Business Hour Metrics
    if (Object.keys(data.businessHourMetrics || {}).length) {
      formattedData.businessHourMetrics = {
        data: Object.entries(data.businessHourMetrics).map(([subject, values]) => ({
          subject,
          Automated: values.Automated ? +values.Automated.toFixed(2) : 0,
          Manual: values.Manual ? +values.Manual.toFixed(2) : 0,
          fullMark: 100,
        })),
      };
    }

    // 10) Revenue Trends
    if (data.revenueTrends) {
      formattedData.revenueTrends = {
        data: data.revenueTrends || {},
      };
    }

    // 11) Collection Trends
    if (data.collectionTrends) {
      formattedData.collectionTrends = {
        data: data.collectionTrends || {},
      };
    }

    // 12) Health Score & Gauge
    if (data.healthScoreData) {
      const healthScore =
        data.healthScoreData?.length > 0 ? data.healthScoreData[0]?.value : 0;
      // find color:
      const selectedColor = severityColors.find(
        (v) => healthScore >= v.min && healthScore <= v.max
      );

      formattedData.healthScoreData = {
        data: data.healthScoreData,
      };

      formattedData.gaugeData = {
        data: [
          { name: "Value", value: healthScore },
          { name: "Remaining", value: 100 - healthScore },
        ],
        legend: false,
        value:
          healthScore === "0"
            ? [{ text: "Unknown", color: "#777", fontSize: "1.2em" }]
            : [
                { text: healthScore, color: selectedColor?.color || "#777" },
                {
                  text: selectedColor?.label || "N/A",
                  color: selectedColor?.color || "#777",
                  fontSize: "1.0em",
                },
              ],
        chartColors: [selectedColor?.color || "#777", "#FFFFFF"],
        caption:
          "This score is based on the treatments recommended by your dentist and how much time has passed since they were prescribed.",
        explanation:
          data.healthScoreData?.length && data.healthScoreData[0].caption
            ? data.healthScoreData[0].caption
            : "",
        // Add the action with loading/disabled states
        action:
          healthScore !== "0" && {
            text: "See Details",
            click: openHealthScoreModal,
            isLoading: isHealthScoreModalLoading,
            isDisabled: isHealthScoreModalLoading,
          },
      };
    }

    // 13) Treatment History
    if (data.treatmentHistory) {
      formattedData.treatmentHistory = {
        data: data.treatmentHistory,
        caption:
          "This chart provides an overview of your completed treatment history, showing the types and quantities of treatments you have received.",
      };
    }

    // 14) Unscheduled Treatments
    if (data.unscheduledTreatments) {
      formattedData.unscheduledTreatments = {
        data: data.unscheduledTreatments,
        caption:
          "This chart provides an overview of your incomplete treatments, showing the types and quantities of treatments that you need to do.",
      };
    }

    // 15) Account Balance
    if (data.accountBalanceData) {
      formattedData.accountBalanceData = {
        data: data.accountBalanceData,
        caption:
          "This chart shows your account balance, indicating the amounts paid and outstanding for your dental treatments.",
      };
    }

    // 16) Insurance Coverage
    if (data.insuranceCoverageData) {
      formattedData.insuranceCoverageData = {
        data: data.insuranceCoverageData,
        caption:
          "This chart illustrates your insurance coverage, showing the used and remaining benefits for the current period.",
      };
    }

    // 17) Health Score Trend
    if (data.healthScoreTrend) {
      formattedData.healthScoreTrend = {
        data: data.healthScoreTrend,
        caption:
          "This line chart shows the trend of your oral health score over time, helping you track improvements or changes in your oral health.",
      };
    }

    // 18) Office Goal
    if (data.officeGoal) {
      formattedData.officeGoal = {
        data: data.officeGoal.goal,
        caption: data.officeGoal.caption,
        unit: "$",
      };
    }
    
    if (data.aiProduction) {
      formattedData.aiProduction = {
        data: data.aiProduction.goal,
        caption: data.aiProduction.caption,
        unit: "$",
      };
    }

    return formattedData;
  };

  // ------------------ Alert Actions ------------------
  const handleAlertAction = async (action) => {
    if (action.path) {
      navigate(
        action.path +
          (action.path.includes("?") ? "&" : "?") +
          window.location.search.split("?")[1]
      );
      return;
    }
    if (action.url) {
      const response = await cqAPI.get(action.url);
      if (response.type === "ok") {
        await fetchDashboardData();
        return;
      }
      if (response.msg) {
        toast({
          title: response.type === "error" ? "Error" : "Success",
          description: response.msg,
          status: response.type === "error" ? "error" : "success",
          duration: 9000,
          isClosable: true,
        });
      }
    }
  };

  // ------------------ Render ------------------
  if (!dashboardData) {
    return <Text>Loading...</Text>;
  }

  // Adapt the raw data for the various chart components
  const formattedData = formatDashboardData(dashboardData);

  // Define the bars for staff performance chart
  const staffPerformanceBars = [
    { dataKey: "SMS", stackId: "a", fill: theme.colors.brand[30], name: "SMS" },
    { dataKey: "Emails", stackId: "a", fill: theme.colors.brand[20], name: "Emails" },
  ];

  // List of chart configurations
  const chartConfigs = [
    {
      dataKey: "aiProduction",
      component: ProgressBar,
      title: "Production from AI",
      condition: (data) =>
        data.aiProduction && data.aiProduction.data && data.aiProduction.data.length > 0,
    },
    {
      dataKey: "officeGoal",
      component: ProgressBar,
      title: "Office Goal",
      condition: (data) =>
        data.officeGoal && data.officeGoal.data && data.officeGoal.data.length > 0,
    },
    {
      dataKey: "communicationData",
      component: Chart,
      title: "Patient Communication Overview",
      condition: (data) =>
        data.communicationData &&
        data.communicationData.data.some(
          (x) =>
            ["Calls", "Sms", "Email", "Voicemail"].includes(x.name) &&
            x.value !== null &&
            x.value > 0
        ),
    },
    {
      dataKey: "commonTopics",
      component: Chart,
      title: "Common Topics Discussed",
      condition: (data) => data.commonTopics && data.commonTopics.data.length > 0,
    },
    {
      dataKey: "appointmentData",
      component: Chart,
      title: "Appointment Statuses",
      condition: (data) => data.appointmentData && data.appointmentData.data.length > 0,
    },
    {
      dataKey: "responseTimes",
      component: BarChart,
      title: "Response Time Metrics (hours)",
      bars: [{ dataKey: "ResponseTime", fill: theme.colors.brand[30], name: "Response Time" }],
      yLabel: "hours",
      condition: (data) =>
        data.responseTimes &&
        data.responseTimes.data.length > 0 &&
        data.responseTimes.data.some((x) => x.ResponseTime > 0),
    },
    {
      dataKey: "extCalls",
      component: BarChart,
      title: "Staff Performance (Phone Extensions)",
      bars: [{ dataKey: "value", fill: theme.colors.brand[30], name: "Calls" }],
      yLabel: "number of calls",
      condition: (data) => data.extCalls && data.extCalls.data.length > 0,
    },
    {
      dataKey: "staffPerformance",
      component: BarChart,
      title: "Staff Performance Metrics",
      bars: staffPerformanceBars,
      yLabel: "number of messages",
      condition: (data) =>
        data.staffPerformance && data.staffPerformance.data.length > 0,
    },
    {
      dataKey: "patientSatisfaction",
      component: RadarChart,
      dataKeys: ["Satisfaction"],
      title: "Patient Satisfaction Indicators",
      condition: (data) =>
        data.patientSatisfaction &&
        data.patientSatisfaction.data.length > 0 &&
        data.patientSatisfaction.data.some((x) => x.Satisfaction > 0),
    },
    {
      dataKey: "businessHourMetrics",
      component: RadarChart,
      title: "Quality of Answering Calls During Business Hours",
      dataKeys: ["Automated", "Manual"],
      condition: (data) =>
        data.businessHourMetrics &&
        data.businessHourMetrics.data.length > 0 &&
        (data.businessHourMetrics.data.some((x) => x.Automated > 0) ||
          data.businessHourMetrics.data.some((x) => x.Manual > 0)),
    },
    {
      dataKey: "afterHourMetrics",
      component: RadarChart,
      title: "Quality of Answering Calls After Business Hours",
      dataKeys: ["Automated", "Manual"],
      condition: (data) =>
        data.afterHourMetrics &&
        data.afterHourMetrics.data.length > 0 &&
        (data.afterHourMetrics.data.some((x) => x.Automated > 0) ||
          data.afterHourMetrics.data.some((x) => x.Manual > 0)),
    },
    {
      dataKey: "revenueTrends",
      component: LineChart,
      title: "Production Trends Comparison",
      lines,
      condition: (data) =>
        data.revenueTrends &&
        data.revenueTrends.data &&
        (data.revenueTrends.data["CurrentYear"].length > 0 ||
          data.revenueTrends.data["PreviousYear"].length > 0),
    },
    {
      dataKey: "collectionTrends",
      component: LineChart,
      title: "Collection Trends Comparison",
      lines,
      condition: (data) =>
        data.collectionTrends &&
        data.collectionTrends.data &&
        (data.collectionTrends.data["CurrentYear"].length > 0 ||
          data.collectionTrends.data["PreviousYear"].length > 0),
    },
    {
      dataKey: "gaugeData",
      component: ProgressBar,
      title: "Oral Health Score",
      condition: (data) => data.gaugeData && data.gaugeData.data.length > 0,
    },
    {
      dataKey: "treatmentHistory",
      component: Chart,
      title: "Treatment History",
      condition: (data) =>
        data.treatmentHistory && data.treatmentHistory.data.length > 0,
    },
    {
      dataKey: "unscheduledTreatments",
      component: Chart,
      title: "Unscheduled Treatments",
      condition: (data) =>
        data.unscheduledTreatments && data.unscheduledTreatments.data.length > 0,
    },
    {
      dataKey: "accountBalanceData",
      component: Chart,
      title: "Account Balance",
      condition: (data) =>
        data.accountBalanceData && data.accountBalanceData.data.length > 0,
    },
    {
      dataKey: "insuranceCoverageData",
      component: Chart,
      title: "Insurance Coverage",
      condition: (data) =>
        data.insuranceCoverageData && data.insuranceCoverageData.data.length > 0,
    },
    {
      dataKey: "healthScoreTrend",
      component: LineChart,
      title: "Health Score Trend",
      lines,
      condition: (data) =>
        data.healthScoreTrend &&
        data.healthScoreTrend.data &&
        Object.keys(data.healthScoreTrend.data).length > 0,
    },
  ];

  return (
    <Box className="dashboard-container">
      {Object.keys(dashboardData || {}).length > 0 && (<Text className="page-title dashboard-title">Dashboard (last 30 days)</Text>)}

      {/* Alert for open conversations (staff side only) */}
      {!isPatient && alertData && !!alertData.numOpenConversations && (
        <Box display="flex" justifyContent="center" className="alert-info">
          <Alert status="info" variant="info" alignItems="flex-start" textAlign="left">
            <Box className="alert-icon-wrapper">
              <AlertIcon />
            </Box>
            {`Staff needs to respond to ${alertData.numOpenConversations} emails and SMS messages`}
            {alertData.daysSinceOldestConversation
              ? ` older than ${alertData.daysSinceOldestConversation} days.`
              : `.`}
          </Alert>
        </Box>
      )}

      {/* Additional alerts from dashboardData */}
      {dashboardAlerts &&
        dashboardAlerts.map((alertObject, index) => (
          <Box display="flex" justifyContent="center" className="alert-info" key={index}>
            <Alert status="info" variant="info" alignItems="center" textAlign="left">
              <Box
                className="alert-icon-wrapper"
                color={
                  alertObject.priority === "high"
                    ? "red.500"
                    : alertObject.priority === "medium"
                    ? "yellow.500"
                    : undefined
                }
              >
                <AlertIcon />
              </Box>
            <Flex flex="1" align="center">
                <Text>{alertObject.msg}</Text>
                {alertObject.action && (
                  <Button
                    ml="auto"
                    width="auto"
                    minW="unset"
                    onClick={() => handleAlertAction(alertObject.action)}
                  >
                    {alertObject.action.text}
                  </Button>
                )}
              </Flex>
            </Alert>
          </Box>
        ))}

      {/* Main Chart Grid */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 5, md: 10 }}>
        {chartConfigs.map((config, index) => {
          const { dataKey, component: Component, title, condition, ...configRest } = config;
          const chartDataEntry = formattedData[dataKey];

          if (condition(formattedData)) {
            const { data, ...dataRest } = chartDataEntry;
            const componentProps = { ...configRest, ...dataRest };
            return <Component key={index} data={data} title={title} {...componentProps} />;
          }
          return null;
        })}
      </SimpleGrid>

      {/* ------------------ Modal for Incomplete Treatments ------------------ */}
      <Modal isOpen={isHealthScoreModalOpen} onClose={closeHealthScoreModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Incomplete Treatments</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <ProgressBar
              data={
                simulatedScore.score
                  ? [
                      { name: "Value", value: simulatedScore.score },
                      { name: "Remaining", value: 100 - simulatedScore.score },
                    ]
                  : []
              }
              chartColors={[simulatedColor?.color || "#777", "#FFFFFF"]}
              value={[
                { text: simulatedScore.score, color: simulatedColor?.color || "#777" },
                {
                  text: simulatedColor?.label || "N/A",
                  color: simulatedColor?.color || "#777",
                  fontSize: "1.0em",
                },
              ]}
              legend={false}
              caption={
                "Select which treatments you would like to complete to see the impact on your score."
              }
            />
            {incompleteTreatments.length > 0 ? (
              incompleteTreatments
                // reduce to ensure multiple of same type are flagged
                .reduce((acc, cur) => {
                  const existing = acc.find(
                    (a) =>
                      a.name === cur.name &&
                      a.added_date === cur.added_date &&
                      a.proc_code === cur.proc_code
                  );
                  if (!existing) {
                    acc.push(cur);
                  } else {
                    existing.multiple = true;
                  }
                  return acc;
                }, [])
                .map((treatment, idx) => {
                  const bubbleColor = getSeverityColor(treatment.severity || 1);
                  return (
                    <Box key={idx} display="flex" alignItems="center" my={2}>
                      {/* Severity bubble */}
                      <Box bg={bubbleColor} borderRadius="50%" width="14px" height="14px" mr={3} />
                      <Checkbox
                        isChecked={selectedTreatments.includes(treatment.id)}
                        onChange={() => handleTreatmentToggle(treatment.id)}
                        mr={3}
                        isDisabled={isHealthScoreModalLoading}
                      />
                      <Text>
                        {treatment.multiple ? "Multiple " : ""}
                        {treatment.name} {treatment.added_date? ` added on ${treatment.added_date}` : ''}
                      </Text>
                    </Box>
                  );
                })
            ) : (
              <Text>No incomplete treatments found.</Text>
            )}
          </ModalBody>

          {/* Footer with a single Link on the left, a single Button on the right */}
          <ModalFooter>
            <Flex display="flex" alignItems="center" justifyContent="space-between" w="100%">
              <ChakraLink color="blue.500" textDecoration="underline" onClick={handleMakeAppointment}>
                Make an Appointment
              </ChakraLink>

              {/* "Ask for More Info" as a button on the right */}
              <Button colorScheme="blue" onClick={handleAskForMoreInfo}>
                Ask for More Info
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
